import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTripContext } from './TripDetailPage';
import { expensesApi, type ExtractedExpense } from '../../api/expenses.api';
import type { Expense, ExpenseDebt, BudgetLimit, BudgetStatusItem, RedistributionPreview } from '../../types';
import Modal from '../../components/ui/Modal';
import LoadingScreen from '../../components/ui/LoadingScreen';
import { toast } from '../../components/ui/Toast';
import { isAlreadyGone } from '../../api/client';
import ExpenseForm from '../../components/expenses/ExpenseForm';
import ScanExpensesModal from '../../components/expenses/ScanExpensesModal';
import { CurrencySelect } from '../../components/ui/CurrencySelect';
import { tripsApi } from '../../api/trips.api';
import { Icon, Plus, Sparkles } from '../../components/ui/Icon';
import { CategoryDonut } from '../../components/trips/expenses/CategoryDonut';
import { Balances } from '../../components/trips/expenses/Balances';
import { LedgerTable } from '../../components/trips/expenses/LedgerTable';
import { SpendingByPerson } from '../../components/trips/expenses/SpendingByPerson';
import { SpendingByDay } from '../../components/trips/expenses/SpendingByDay';
import { BudgetOverview } from '../../components/trips/expenses/BudgetOverview';
import { BudgetAlerts } from '../../components/trips/expenses/BudgetAlerts';
import { BudgetManager } from '../../components/trips/expenses/BudgetManager';
import { ExpenseRedistributeBanner } from '../../components/trips/expenses/ExpenseRedistributeBanner';
import { tripPeople, useBudgetLabel } from '../../components/trips/expenses/budgetHelpers';
import { summarize, countBy, ledgerPeople } from '../../components/trips/expenses/summarize';
import { LedgerFilterBar, type ActiveFilter } from '../../components/trips/expenses/LedgerFilterBar';
import type { FilterDimension } from '../../components/ui/FilterMenu';
import { catHex } from '../../components/trips/expenses/meta';
import { format, parseISO } from 'date-fns';
import { useDateLocale } from '../../utils/dateLocale';
import { useAiDataVersion, useAiPanel } from '../../components/ai/AiPanelContext';
import { tripMemberCount, tripMemberUsers } from '../../utils/tripMembers';
import type { AiPersistedAction } from '../../api/aiProposals';
import { tripPermissions } from '../../utils/tripPermissions';

export default function ExpensesTab() {
  const { trip, reload } = useTripContext();
  const { t } = useTranslation();
  const [expenses, setExpenses]       = useState<Expense[]>([]);
  const [debts, setDebts]             = useState<ExpenseDebt[]>([]);
  const [loading, setLoading]         = useState(true);
  const [createOpen, setCreateOpen]     = useState(false);
  // Rows a scan detected but nobody has saved yet — the batch review is open
  // while this is non-empty.
  const [scanned, setScanned]           = useState<ExtractedExpense[] | null>(null);
  const [scanOpen, setScanOpen]         = useState(false);
  const [editExpense, setEditExpense]   = useState<Expense | null>(null);
  const [filterPayer, setFilterPayer]         = useState<string | null>(null);
  const [filterCategory, setFilterCategory]   = useState<string | null>(null);
  const [filterDay, setFilterDay]             = useState<string | null>(null);
  const [currencyOpen, setCurrencyOpen]       = useState(false);
  const [savingCurrency, setSavingCurrency]   = useState(false);
  const [budgetLimits, setBudgetLimits]       = useState<BudgetLimit[]>([]);
  const [budgetStatus, setBudgetStatus]       = useState<BudgetStatusItem[]>([]);
  const [budgetOpen, setBudgetOpen]           = useState(false);
  const [redistribution, setRedistribution]   = useState<RedistributionPreview | null>(null);
  const [redistributing, setRedistributing]   = useState(false);
  const budgetLabel = useBudgetLabel();
  const dateLocale  = useDateLocale();

  // Per-trip dismissal keyed by the exact set of newcomers, so the offer stays
  // hidden for the same people but re-appears if a *different* member later joins.
  const dismissKey = `expenseRedistDismissed:${trip.id}`;
  function newcomerSetKey(p: RedistributionPreview): string {
    return p.newcomers.map(n => n.id).sort().join(',');
  }

  // gIA can propose an expense (create_expense); on accept the dispatcher calls
  // this so the tab refreshes its list, debts and budget status.
  useAiPanel({ context: 'expenses', onAction: handleAiExpense });

  const { canEdit } = tripPermissions(trip);

  async function handleChangeCurrency(next: string) {
    if (next === trip.budgetCurrency) { setCurrencyOpen(false); return; }
    setSavingCurrency(true);
    try {
      await tripsApi.update(trip.id, { budgetCurrency: next });
      reload();
      setCurrencyOpen(false);
      toast(t('expenses.baseCurrency.saved'), 'success');
    } catch {
      toast(t('expenses.baseCurrency.failed'), 'error');
    } finally {
      setSavingCurrency(false);
    }
  }

  async function load(): Promise<BudgetStatusItem[]> {
    // No `summary` call: it sums the whole trip, which is precisely what the
    // cards stop showing once a filter is on. The list is unpaginated, so the
    // totals are computed here from the same rows.
    const [exps, dbs, budgets] = await Promise.all([
      expensesApi.list(trip.id),
      expensesApi.debts(trip.id),
      expensesApi.budgets(trip.id),
    ]);
    setExpenses(exps);
    setBudgetLimits(budgets.limits);
    setBudgetStatus(budgets.status);
    // Auto-populate debts on first load when expenses exist
    if (exps.length > 0 && dbs.length === 0) {
      try {
        await expensesApi.recalculateDebts(trip.id);
        const freshDebts = await expensesApi.debts(trip.id);
        setDebts(freshDebts);
      } catch {
        setDebts(dbs);
      }
    } else {
      setDebts(dbs);
    }
    // Offer to fold members who joined after some global expenses into them.
    // Only editors can apply, so don't bother the rest with the fetch/banner.
    if (canEdit) {
      try {
        const preview = await expensesApi.redistributionPreview(trip.id);
        const dismissed = localStorage.getItem(dismissKey);
        setRedistribution(
          preview.expenseCount > 0 && newcomerSetKey(preview) !== dismissed ? preview : null,
        );
      } catch {
        setRedistribution(null);
      }
    }
    return budgets.status;
  }

  function handleDismissRedistribution() {
    if (redistribution) localStorage.setItem(dismissKey, newcomerSetKey(redistribution));
    setRedistribution(null);
  }

  async function handleApplyRedistribution() {
    setRedistributing(true);
    try {
      const { redistributed } = await expensesApi.applyRedistribution(trip.id);
      localStorage.removeItem(dismissKey);
      setRedistribution(null);
      await load();
      toast(t('expenses.redistribute.done', { count: redistributed }), 'success');
    } catch {
      toast(t('expenses.redistribute.failed'), 'error');
    } finally {
      setRedistributing(false);
    }
  }

  // Toast when saving an expense pushes a limit into a worse state — over ("you
  // just went over") or, one step earlier, warn ("you're getting close"). The
  // server's status is the source of truth; we diff each limit's state by id
  // against what it was before, so we alert only on the crossing, not every save.
  async function reloadAndAlert() {
    const prevById = new Map(budgetStatus.map(s => [s.id, s.state]));
    const fresh = await load();
    const budgetPeople = tripPeople(trip);
    const newlyOver = fresh.filter(s => s.state === 'over' && prevById.get(s.id) !== 'over');
    const newlyWarn = fresh.filter(s => s.state === 'warn' && (prevById.get(s.id) ?? 'ok') === 'ok');
    if (newlyOver.length > 0) {
      toast(t('expenses.budget.overToast', { label: budgetLabel(newlyOver[0], budgetPeople) }), 'error');
    } else if (newlyWarn.length > 0) {
      toast(t('expenses.budget.warnToast', { label: budgetLabel(newlyWarn[0], budgetPeople) }), 'info');
    }
  }

  useEffect(() => {
    load().catch(() => toast(t('expenses.toast.loadFailed'), 'error')).finally(() => setLoading(false));
  }, [trip.id]);

  // ADR-0028: an accepted card edited an expense on the server; re-read and alert.
  const aiExpensesVersion = useAiDataVersion('expenses');
  useEffect(() => {
    if (aiExpensesVersion > 0) void reloadAndAlert();
  }, [aiExpensesVersion]);

  async function handleSettle(debtId: string, note?: string | null) {
    await expensesApi.settleDebt(trip.id, debtId, note);
    await load();
    toast(t('expenses.toast.settled'), 'success');
  }

  async function handleDelete(id: string) {
    try {
      await expensesApi.delete(trip.id, id);
    } catch (e) {
      if (!isAlreadyGone(e)) { toast(t('common.error'), 'error'); return; }
    }
    setEditExpense(null);
    await expensesApi.recalculateDebts(trip.id);
    await load();
    toast(t('expenses.toast.deleted'), 'success');
  }

  async function handleCreated(expense: Expense) {
    setCreateOpen(false);
    await expensesApi.recalculateDebts(trip.id);
    await reloadAndAlert();
  }

  function closeScan() {
    setScanOpen(false);
    setScanned(null);
  }

  async function handleScannedSaved(created: Expense[]) {
    closeScan();
    if (created.length === 0) return;
    await expensesApi.recalculateDebts(trip.id);
    await reloadAndAlert();
  }

  async function handleAiExpense(actions: AiPersistedAction[]) {
    // An accepted budget proposal already returns the fresh limits; a new expense
    // needs a debt recompute first (a deletion already did its own — see
    // persistExpenseDeleteProposal). Either way, reload so the list, balances and
    // bars/alerts reflect it.
    const hasNewExpense = actions.some(a => a.type === 'expense_created');
    const touchesLedger = hasNewExpense
      || actions.some(a => a.type === 'expenses_deleted' || a.type === 'budget_set');
    if (!touchesLedger) return;
    if (hasNewExpense) await expensesApi.recalculateDebts(trip.id);
    await reloadAndAlert();
  }

  async function handleUpdated(expense: Expense) {
    setEditExpense(null);
    await expensesApi.recalculateDebts(trip.id);
    await reloadAndAlert();
  }

  if (loading) return <LoadingScreen />;

  const currency       = trip.budgetCurrency || 'EUR';
  const canEditCurrency = canEdit && expenses.length === 0;
  // The server already returns them newest day first and, within a day, in the
  // order they were created — re-sorting here by date alone would only re-tie
  // same-day expenses and lose that.
  // Three filters, and every control counts under the OTHER two — never under
  // its own. Applying the day filter to the day pills would leave the chosen
  // day as the only one on screen, with no way back to another.
  const byPayer    = (e: Expense) => !filterPayer || e.payerId === filterPayer;
  const byCategory = (e: Expense) => !filterCategory || e.category === filterCategory;
  const byDay      = (e: Expense) => !filterDay || e.date.slice(0, 10) === filterDay;

  const visibleExpenses  = expenses.filter(e => byPayer(e) && byCategory(e) && byDay(e));
  const categoryFacet    = expenses.filter(e => byPayer(e) && byDay(e));
  const dayFacet         = expenses.filter(e => byPayer(e) && byCategory(e));
  const payerFacet       = expenses.filter(e => byCategory(e) && byDay(e));

  const summary        = summarize(visibleExpenses);
  const categoryCounts = countBy(categoryFacet, e => e.category);
  const payerCounts    = countBy(payerFacet, e => e.payerId);
  const dayCounts      = countBy(dayFacet, e => e.date.slice(0, 10))
    .sort(([a], [b]) => b.localeCompare(a)); // newest first, like the ledger

  const biggestAmount  = visibleExpenses.length > 0
    ? Math.max(...visibleExpenses.map(e => Number(e.amount)))
    : 0;

  const dayLabel = (day: string) => format(parseISO(day), 'd MMM', { locale: dateLocale });
  const categoryLabel = (cat: string) => t(`expenses.categories.${cat}` as Parameters<typeof t>[0]);

  // Members, plus anybody who paid and has since been removed: their expenses
  // stay in the total, so their row has to stay in the table.
  const people = ledgerPeople(tripMemberUsers(trip), expenses);
  const memberName = (id: string) => {
    const person = people.find(p => p.id === id);
    return person ? (person.displayName || person.username) : id;
  };

  // One entry per dimension. A filter added later — payment method, an amount
  // range — is one more entry here and nothing else: that is what this shape
  // buys over a row of pills per dimension.
  const filterDimensions: FilterDimension[] = [
    {
      key: 'day',
      label: t('expenses.filters.day'),
      active: filterDay,
      options: dayCounts.map(([day, count]) => ({ value: day, label: dayLabel(day), count })),
    },
    {
      key: 'category',
      label: t('expenses.filters.category'),
      active: filterCategory,
      options: categoryCounts.map(([cat, count]) => ({
        value: cat, label: categoryLabel(cat), count, color: catHex(cat),
      })),
    },
    {
      key: 'payer',
      label: t('expenses.filters.paidBy'),
      active: filterPayer,
      options: payerCounts.map(([id, count]) => ({ value: id, label: memberName(id), count })),
    },
  ];

  function pickFilter(dimension: string, value: string | null) {
    if (dimension === 'day') setFilterDay(value);
    if (dimension === 'category') setFilterCategory(value);
    if (dimension === 'payer') setFilterPayer(value);
  }

  const activeFilters: ActiveFilter[] = [
    ...(filterDay ? [{
      id: `day:${filterDay}`,
      dimensionLabel: t('expenses.filters.day'),
      valueLabel: dayLabel(filterDay),
      onRemove: () => setFilterDay(null),
    }] : []),
    ...(filterCategory ? [{
      id: `category:${filterCategory}`,
      dimensionLabel: t('expenses.filters.category'),
      valueLabel: categoryLabel(filterCategory),
      color: catHex(filterCategory),
      onRemove: () => setFilterCategory(null),
    }] : []),
    ...(filterPayer ? [{
      id: `payer:${filterPayer}`,
      dimensionLabel: t('expenses.filters.paidBy'),
      valueLabel: memberName(filterPayer),
      onRemove: () => setFilterPayer(null),
    }] : []),
  ];

  function clearFilters() {
    setFilterCategory(null);
    setFilterDay(null);
    setFilterPayer(null);
  }

  return (
    <div className="flex flex-col h-full overflow-hidden animate-fade-in">
      {/* Header */}
      <div className="shrink-0 px-4 md:px-8 pt-5 pb-4 bg-white dark:bg-slate-900 border-b border-slate-100 dark:border-slate-800">
        <div className="flex items-center justify-between">
          <div>
            <p className="font-mono text-[10px] tracking-[0.14em] uppercase text-slate-400 mb-0.5">
              {trip.destination.toUpperCase()} · {t('expenses.travellersCount', { count: tripMemberCount(trip) })} ·{' '}
              {canEditCurrency ? (
                <button
                  onClick={() => setCurrencyOpen(true)}
                  className="underline decoration-dotted underline-offset-2 hover:text-teal-600 cursor-pointer"
                  title={t('expenses.baseCurrency.edit')}
                >
                  {currency}
                </button>
              ) : (
                <span>{currency}</span>
              )}
            </p>
            <h2
              className="font-serif italic font-semibold text-2xl md:text-3xl text-slate-900 dark:text-slate-100"
              style={{  }}
            >
              {t('expenses.ledger')}
            </h2>
          </div>
          <div className="hidden md:flex items-center gap-2 shrink-0">
            {canEdit && (
              <button
                onClick={() => setScanOpen(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-teal-200 dark:border-teal-800 bg-teal-50 dark:bg-teal-900/20 text-teal-700 dark:text-teal-300 hover:bg-teal-100 dark:hover:bg-teal-900/40 text-xs font-semibold transition-colors cursor-pointer"
              >
                <Icon as={Sparkles} size={14} />
                {t('expenses.scan.aiButton')}
              </button>
            )}
            <button
              onClick={() => setCreateOpen(true)}
              className="btn-primary gap-1.5"
            >
              <Icon as={Plus} size={16} />
              {t('expenses.addExpense')}
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto px-4 md:px-8 py-5 md:py-6">
        <div className="space-y-5">
          {/* Mobile add buttons */}
          <div className="md:hidden flex gap-2">
            <button
              onClick={() => setCreateOpen(true)}
              className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl border-2 border-dashed border-teal-300 dark:border-teal-700 text-teal-600 dark:text-teal-400 text-sm font-semibold hover:bg-teal-50 dark:hover:bg-teal-900/20 transition-colors cursor-pointer"
            >
              <Icon as={Plus} size={16} />
              {t('expenses.addExpense')}
            </button>
            {canEdit && (
              <button
                onClick={() => setScanOpen(true)}
                aria-label={t('expenses.scan.aiButton')}
                className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl border-2 border-dashed border-teal-300 dark:border-teal-700 text-teal-600 dark:text-teal-400 text-sm font-semibold hover:bg-teal-50 dark:hover:bg-teal-900/20 transition-colors cursor-pointer"
              >
                <Icon as={Sparkles} size={16} />
              </button>
            )}
          </div>

          {/* A member joined after some global expenses were logged — offer to
              fold them into the equal split (custom splits are left alone). */}
          {canEdit && redistribution && (
            <ExpenseRedistributeBanner
              preview={redistribution}
              busy={redistributing}
              onApply={handleApplyRedistribution}
              onDismiss={handleDismissRedistribution}
            />
          )}

          {/* Budget alerts stay at the top — they only render when a limit is over
              or close, so they read as an exception, not as the tab's headline. The
              full budget overview lives lower, below "who owes whom". */}
          {budgetLimits.length > 0 && <BudgetAlerts status={budgetStatus} trip={trip} />}

          {expenses.length > 0 && (
            <>
              <LedgerFilterBar
                dimensions={filterDimensions}
                active={activeFilters}
                visibleCount={visibleExpenses.length}
                onPick={pickFilter}
                onClear={clearFilters}
              />

              {/* Stats strip */}
              {/* No "per person" tile: total ÷ travellers is an average that
                  claims everyone spent the same, which is exactly what the
                  per-person table below disproves. */}
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
                {[
                  { label: t('expenses.stats.totalSpent'),  value: summary.total.toFixed(2),                               showCurrency: true  },
                  { label: t('expenses.stats.expenses'),     value: String(summary.count),                                  showCurrency: false },
                  { label: t('expenses.stats.biggest'),      value: biggestAmount.toFixed(2),                               showCurrency: true  },
                ].map(stat => (
                  <div key={stat.label} className="card p-4 flex flex-col gap-1 min-w-0">
                    <p className="font-mono text-xs tracking-[0.1em] uppercase text-slate-500 dark:text-slate-400">{stat.label}</p>
                    <div className="flex items-baseline gap-1.5 min-w-0">
                      {stat.showCurrency && (
                        <span className="font-mono text-[10px] font-semibold text-slate-400 shrink-0">{currency}</span>
                      )}
                      <span className="font-mono text-lg md:text-xl xl:text-2xl font-bold tabular-nums text-slate-900 dark:text-slate-100 leading-none truncate">
                        {stat.value}
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              {/* Card grid: donut + spending by day */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <CategoryDonut byCategory={summary.byCategory} total={summary.total} currency={currency} />
                {expenses.length > 1
                  ? <SpendingByDay
                      expenses={dayFacet}
                      currency={currency}
                      selectedDay={filterDay}
                      onSelectDay={day => setFilterDay(prev => (prev === day ? null : day))}
                    />
                  : <div />
                }
              </div>

              {/* What each traveller actually spent: what they laid out, what
                  their share comes to, and the difference — which is the same
                  number the balances below settle pair by pair. */}
              <SpendingByPerson
                people={people}
                byPayer={summary.byPayer}
                byParticipant={summary.byParticipant}
                currency={currency}
              />

              {/* Who owes whom — full width, the tab's headline */}
              <div data-tour="expenses-balances">
                <Balances debts={debts} onSettle={handleSettle} />
              </div>
            </>
          )}

          {/* Ledger table */}
          <LedgerTable
            expenses={visibleExpenses}
            totalCount={expenses.length}
            onSelect={setEditExpense}
            onAdd={canEdit ? () => setCreateOpen(true) : undefined}
          />

          {/* Budget overview — last: it's the reference you go looking for, not
              something to scroll past on the way to the ledger. Over-budget
              limits still surface at the top via BudgetAlerts. */}
          {(budgetLimits.length > 0 || canEdit) && (
            <BudgetOverview
              status={budgetStatus}
              trip={trip}
              canEdit={canEdit}
              onEdit={() => setBudgetOpen(true)}
            />
          )}
        </div>

        <Modal open={createOpen} onClose={() => setCreateOpen(false)} title={t('expenses.addExpense')} size="lg">
          <ExpenseForm
            tripId={trip.id}
            trip={trip}
            onSaved={handleCreated}
            onCancel={() => setCreateOpen(false)}
            onMultipleScanned={rows => { setCreateOpen(false); setScanned(rows); setScanOpen(true); }}
          />
        </Modal>
        <Modal open={currencyOpen} onClose={() => setCurrencyOpen(false)} title={t('expenses.baseCurrency.title')}>
          <div className="space-y-3">
            <p className="text-sm text-slate-500 dark:text-slate-400">{t('expenses.baseCurrency.desc')}</p>
            <CurrencySelect value={trip.budgetCurrency || 'EUR'} onChange={handleChangeCurrency} />
            {savingCurrency && <p className="text-xs text-slate-400">{t('expenses.baseCurrency.saving')}</p>}
          </div>
        </Modal>
        <Modal open={budgetOpen} onClose={() => setBudgetOpen(false)} title={t('expenses.budget.title')} size="lg">
          <BudgetManager
            trip={trip}
            limits={budgetLimits}
            onSaved={res => { setBudgetLimits(res.limits); setBudgetStatus(res.status); setBudgetOpen(false); }}
            onCancel={() => setBudgetOpen(false)}
          />
        </Modal>
        {scanOpen && (
          <ScanExpensesModal
            tripId={trip.id}
            trip={trip}
            initialExpenses={scanned}
            onClose={closeScan}
            onSaved={handleScannedSaved}
          />
        )}
        <Modal open={!!editExpense} onClose={() => setEditExpense(null)} title={t('expenses.editExpense')} size="lg">
          {editExpense && (
            <ExpenseForm
              tripId={trip.id}
              trip={trip}
              existing={editExpense}
              onSaved={handleUpdated}
              onCancel={() => setEditExpense(null)}
              onDelete={() => handleDelete(editExpense.id)}
            />
          )}
        </Modal>
      </div>
    </div>
  );
}
